import React, { useState, useEffect, use } from "react";
import uuidv4 from "uuid/v4";
import Uppy from '@uppy/core';
import AwsS3 from "@uppy/aws-s3";
import { Button, Form, Row, Col } from "react-bootstrap";
import Select from 'react-select';
import ImageUploader from "../../components/settings/ImageUploader";
import imgCamelLogo from '../../assets/images/main-logo-white.png';
import "./CreateCtaForm.scss";
import { normalizeUrl } from "../../utils/common";

const avatarImage = "https://s3.us-west-2.amazonaws.com/static.contentcamel.io/default-avatar-01.svg";

const styleOptions = [
  { value: 'chatBubble', label: 'Message Bubble' },
  { value: 'fullWidth', label: 'Full Width' },
  { value: 'buttonOnly', label: 'Button Only' }
];

/**
 * One time init of Uppy for this component
 *
 * @returns {Uppy}
 */
const initUppy = () => {
  const uppy = new Uppy({
    id: "createBrandingUppy",
    debug: true,
    autoProceed: false,
    allowMultipleUploadBatches: false,
    restrictions: {
      allowedFileTypes: ["image/*"],
    },
    onBeforeUpload: (files) => {
      const updatedFiles = {};
      for (const [fileID, file] of Object.entries(files)) {
        if (file.meta.s3Key) {
          file.meta.key = file.meta.s3Key; 
          file.meta.name = file.meta.s3Key; 
        }
        updatedFiles[fileID] = file; 
      }
  
      return updatedFiles; 
    },
  });

  uppy.use(AwsS3, {
    limit: 3,
    timeout: 60000,
    companionUrl: process.env.REACT_APP_COMPANION_URL,
    companionHeaders: {
      allowedMetaFields: true
    }
  });

  return uppy;
}


const CreateCtaForm = props => {
  const {
    onSubmit: handleSubmitProp,
    onClose,
    isEditing,
    item,
    userInfo
  } = props;
  // State variables
  const [name, setName] = useState("");
  const id = uuidv4(); // create a new ID on the client side for new CTAs
  const [message, setMessage] = useState("");
  const [buttonText, setButtonText] = useState("Click here");
  const [url, setUrl] = useState("");
  const [buttonStyle, setButtonStyle] = useState(styleOptions[0]);
  const [useProfilePicture, setUseProfilePicture] = useState(true);
  const [coverImage, setCoverImage] = useState("");
  const [uppyInstance] = useState(() => initUppy());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // When editing an existing CTA, pre-fill the form fields.
  useEffect(() => {
    if (item) {
      setMessage(item.message || "");
      setUrl(item.targetUrl || "");
      setButtonText(item.buttonText || "Click here");
      setName(item.name || "");
      setUseProfilePicture(
        item.imageUrl === "" || !item.imageUrl ? true : false  
      );
      setCoverImage(item.imageUrl || "");
      const styleOption = styleOptions.find(
        (st) => st.value === item.options?.style
      );
      setButtonStyle(styleOption || styleOptions[0]);
    }
  }, [item]);

  // Cleanup uppy instance on component unmount
  useEffect(() => {
    
    return () => {
      uppyInstance.close();
    };

  }, [uppyInstance]);

  // Configure Uppy before files are added
  useEffect(() => {
    if (!id) return;
    uppyInstance.setOptions({
      onBeforeFileAdded: (currentFile) => {
        const newName = `${userInfo.tenantId}/ctas/${id}/${currentFile.name}`;
        return {
          ...currentFile,
          meta: {
            ...currentFile.meta,
            s3Key: newName,
            tenantId: userInfo.tenantId,
            id: id
          }
        };
      }
    });

  }, [id, uppyInstance, userInfo.tenantId]);

  useEffect(() => {
    if (coverImage) {
      setUseProfilePicture(false);
    } else {
      setUseProfilePicture(true);
    }
    // we have a remote cover image
    if (coverImage && coverImage.startsWith("https://")) {
      if (isSubmitting) {
        submitForm();
        setIsSubmitting(false);
      }
    }
  }, [coverImage]);

  // useEffect(() => {
  //   if (isSubmitting) {
  //   }
  // }, [isSubmitting]);

  // Validate required fields.
  const isInvalid = () => {
    return !name || !message || !buttonText || !url || !buttonStyle;
  };

  

  // Handle form submission.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const pendingFiles = uppyInstance.getFiles();

    if (!pendingFiles || pendingFiles.length === 0) {
      submitForm();
      return;
    }
    try {
      const response = await uppyInstance.upload();
      if (response.successful) {
        const ctaImageResponse = response.successful.find(
          (res) => res.source === `dragdrop-cta`
        );
        if (ctaImageResponse) {
          setCoverImage(ctaImageResponse.uploadURL);
          return; // we handle submit in the useEffect for coverImage (yeah, it's kind of odd)
        }
        submitForm();
      }
    } catch (error) {
      console.error("Error uploading file", error);
      submitForm();
    }
  };

  const submitForm = () => {
    const finalTargetUrl = normalizeUrl(url);
    const ctaData = {
      id,
      name,
      message,
      buttonText,
      targetUrl: finalTargetUrl,
      imageUrl: useProfilePicture ? "" : coverImage,
      type: userInfo?.type || "user",
      style: buttonStyle.value,
      userName: userInfo?.name || "Name",
      useProfilePicture,
    };
    
    if (isEditing && item?.id) {
      ctaData.id = item.id;
    }
    
    handleSubmitProp(ctaData);
    onClose();
  };

  return (
    <Form className="create-cta-form">
      {/* Header */}
      <div className="form-header">
        <img src={imgCamelLogo} alt="logo" />
        <button type="button" className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="main-content">
        <h2 className="title">{isEditing ? "Edit" : "Create"} a Call to Action</h2>

        {/* Form Controls */}
        <div className="create-container">
          <div className="edit-box">
            {/* CTA Name */}
            <section className="form-section">
              <div className="title-description">
                <label htmlFor="formName">CTA Name</label>
                <span>The name is how users will reference the CTA</span>
              </div>
              <Form.Group controlId="formName" className="mb-1">
                <Form.Control
                  type="text"
                  placeholder="Enter your CTA name here"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Group>
            </section>

            {/* Message */}
            <section className="form-section">
              <div className="title-description">
                <label htmlFor="formMessage">Message</label>
                <span>Personalize your call to action (where applicable)</span>
              </div>
              <Form.Group controlId="formMessage" className="mb-1">
                <Form.Control
                  type="text"
                  placeholder="Enter your message here"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Form.Group>
            </section>

            {/* Button Text & Target URL */}
            <section className="form-section">
              <div className="row mb-1">
                <div className="col-md-6">
                  <div className="title-description">
                    <label htmlFor="formButtonText">Button Text</label>
                    <span>Short text for the action</span>
                  </div>
                  <Form.Group controlId="formButtonText">
                    <Form.Control
                      type="text"
                      placeholder="Enter your action button text here"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <div className="title-description">
                    <label htmlFor="formUrl">Target URL</label>
                    <span>The CTA action</span>
                  </div>
                  <Form.Group controlId="formUrl">
                    <Form.Control
                      type="text"
                      placeholder="https://contentcamel.io"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </div>
            </section>

            {/* CTA Image Override */}
            <section className="form-section">
              <div className="title-description">
                <label htmlFor="ctaImagePicker">CTA Image Override</label>
                <span>
                  Upload a custom image to override the user's profile picture. If no image is uploaded, the CTA will
                  use the default profile picture.
                </span>
              </div>
              <Row>
                <Col sm={6}>
                  <div className="logo-picker">
                    <ImageUploader
                      id="ctaImagePicker"
                      user={userInfo}
                      uppy={uppyInstance}
                      type="cta"
                      tenantId={userInfo.tenantId}
                      typeId={id}
                      imageUrl={coverImage}
                      onImageUpdate={setCoverImage}
                      autoProceed
                      brandingName={name}
                    />
                  </div>
                </Col>
              </Row>
            </section>

            {/* CTA Style */}
            <section className="form-section">
              <div className="title-description">
                <label>CTA Style</label>
                <span>How will the CTA appear?</span>
              </div>
              <Select
                classNamePrefix="select"
                options={styleOptions}
                placeholder="Select Button Style"
                value={buttonStyle}
                onChange={setButtonStyle}
              />
            </section>

            {/* Submit Button */}
            <section className="form-section">
              <div className="actions-box">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="mr-2"
                  disabled={isInvalid()}
                  onClick={handleSubmit}
                >
                  {isEditing ? "Edit" : "Create"}
                </Button>
              </div>
            </section>
          </div>
        </div>

        {/* Preview Section */}
        <div className="preview-container">
          <div className="main-view">
            <div className="title-view" />
            <div className="content-view" />
            <div className="content-view" />
            <div className="content-view" />
          </div>
          {buttonStyle.value === "buttonOnly" ? (
            <div className="cta-button-only">
              <div className="action-button">{buttonText}</div>
            </div>
          ) : (
            <div className={`cta-view ${buttonStyle.value === "chatBubble" ? "" : " full-width-style"}`}>
              <div className="profile-image">
                <img src={useProfilePicture ? avatarImage : coverImage} alt="avatar" />
              </div>
              {buttonStyle.value === "chatBubble" && <div className="arrow-left" />}
              <div className="content-view">
                <div className="user-name">{userInfo?.name || "Name"}</div>
                <div className="action-view">
                  <div className="message-content">{message || "Enter your message here"}</div>
                  <div className="action-button">{buttonText}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Form>
  );
};

export default CreateCtaForm;